import type { AnalyticsEvent } from "@/types/models";

const SNAP_EVENT_STORAGE_PREFIX = "awraq:snap-event:";
const ACQUISITION_PATHS = new Set(["/", "/paper-tree"]);
const sentEvents = new Set<string>();
let configuredPixelId: string | null = null;

/** The image pixel measures explicit events without giving a third-party script access to family data or forms. */
export function initializeSnapPixel(pixelId?: string | null): void {
  configuredPixelId =
    pixelId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(pixelId) ? pixelId : null;
}

function sendEvent(name: string, path: string, eventId?: string): void {
  if (!configuredPixelId) {
    return;
  }

  const url = new URL("https://tr.snapchat.com/p");
  url.searchParams.set("pid", configuredPixelId);
  url.searchParams.set("ev", name);
  url.searchParams.set("pl", new URL(path, window.location.origin).href);
  url.searchParams.set("v", "3.4.0");

  if (eventId) {
    url.searchParams.set("cdid", eventId);
  }

  const pixel = new Image(1, 1);
  pixel.referrerPolicy = "no-referrer";
  pixel.src = url.href;
}

export function trackSnapPage(pageUrl: string): void {
  const page = new URL(pageUrl, window.location.origin);

  // Family pages and authentication URLs can contain names, contact values and bearer tokens.
  if (page.origin !== window.location.origin || !ACQUISITION_PATHS.has(page.pathname)) {
    return;
  }

  // Inertia's navigate event owns the initial visit, later visits and browser history.
  sendEvent("PAGE_VIEW", page.pathname);
}

export function trackSnapEventOnce(event?: AnalyticsEvent | null): void {
  if (!configuredPixelId || !event || event.name !== "sign_up" || sentEvents.has(event.id)) {
    return;
  }

  const storageKey = `${SNAP_EVENT_STORAGE_PREFIX}${event.id}`;

  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }
  } catch {
    // Storage may be disabled; the in-memory guard still deduplicates this visit.
  }

  // Only the successful registration response supplies this event. Never copy the current URL or form values.
  sendEvent("SIGN_UP", "/auth/register/info", event.id);
  sentEvents.add(event.id);

  try {
    sessionStorage.setItem(storageKey, "1");
  } catch {
    // persistence-best-effort: the in-memory guard deduplicates; storage failure must not interrupt onboarding.
  }
}
